import { useMemo } from "react";
import { Chip, Stack } from "@mui/material";
import { DataTable, type Column } from "@/components/DataTable";
import type { Indicador } from "@/components/Indicadores";
import { RelatorioLayout } from "@/components/escala/RelatorioLayout";
import { FiltroRelatorio } from "@/components/escala/RelatorioFiltroCampos";
import { porNome, useRelatorio } from "@/components/escala/useRelatorio";
import { distintos, plural } from "@/components/escala/relatorio";
import { formatDataBR, relSemEscala, rotuloLoja, type SemEscala } from "@/api/escala.service";


const admitidoNoMes = (l: SemEscala, ano: number, mes: number) =>
  l.data_admissao.startsWith(`${ano}-${String(mes).padStart(2, "0")}-`);

function indicadoresSemEscala(linhas: SemEscala[], ano: number, mes: number, totalLojas: number): [Indicador, Indicador, Indicador, Indicador] {
  const admitidos = linhas.filter((l) => admitidoNoMes(l, ano, mes)).length;
  return [
    {
      titulo: "Sem escala aprovada",
      valor: linhas.length,
      nota: linhas.length ? "ativos que entram em escala" : "todos os colaboradores escalados",
      tom: linhas.length ? "atencao" : "bom",
    },
    { titulo: "Admitidos antes do mês", valor: linhas.length - admitidos, nota: "já deveriam estar numa escala aprovada" },
    { titulo: "Admitidos no mês", valor: admitidos, nota: "podem ter entrado depois da escala gerada" },
    { titulo: "Lojas com pendência", valor: distintos(linhas, (l) => l.loja_id), nota: `de ${plural(totalLojas, "loja")}` },
  ];
}

// Colaboradores que já existiam na competência mas não têm nenhum dia em escala aprovada.
export function EscalaRelSemEscalaTab() {
  const r = useRelatorio({ buscar: relSemEscala, ordenar: porNome, mensagemErro: "Erro ao carregar o relatório de colaboradores sem escala" });
  const { ano, mes } = r.filtros;
  const indicadores = useMemo(
    () => indicadoresSemEscala(r.linhas, ano, mes, r.lojas.length),
    [r.linhas, ano, mes, r.lojas.length],
  );

  const columns: Column<SemEscala>[] = [
    { key: "matricula", label: "Matrícula" },
    { key: "nome", label: "Nome" },
    { key: "loja_id", label: "Loja", render: (l) => rotuloLoja(r.lojas, l.loja_id), exportValue: (l) => rotuloLoja(r.lojas, l.loja_id) },
    {
      key: "data_admissao",
      label: "Data de Admissão",
      render: (l) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{formatDataBR(l.data_admissao)}</span>
          {admitidoNoMes(l, ano, mes) && <Chip size="small" color="info" variant="outlined" label="Admitido no mês" />}
        </Stack>
      ),
      exportValue: (l) => formatDataBR(l.data_admissao),
    },
  ];

  return (
    <RelatorioLayout
      carregando={r.carregando}
      filtro={<FiltroRelatorio estado={r} />}
      indicadores={indicadores}
      exportacao={{ colunas: columns, linhas: r.ordenadas, arquivo: "sem-escala", aba: "Sem Escala" }}
      competencia={r.filtros}
    >
      <DataTable columns={columns} {...r.tabela} />
    </RelatorioLayout>
  );
}
